import { useContext, useEffect, useMemo, useState } from "react";
import { ReactComponent as EditIcon } from "../common/icons/edit.svg";
import withFallback from "../common/image-with-fallback";
import stripHtmlToText from "../common/utils/strip-html";
import {
  availableProductVariants,
  getAddedOffers,
  getProductOriginalPrice,
  isProductUnavailableOrLoading,
  updateOffer,
} from "../core/products";
import ProductsContext from "../contexts/ProductsContext";
import RegtriggeringAnimatedCheckmark from "./elements/RegtriggeringAnimatedCheckmark";
import { HTMLSectionWithLink } from "./elements/Link";
import Card, { useThreshold } from "./Card";
import handleConfirmation from "../utils/adaHandler";
import { Offer, Product, Settings, Variant } from "../domain";
import CurrencyContext from "../contexts/CurrencyContext";
import Prices from "./elements/Prices";
import Img from "./elements/Img";
import { getProduct } from "../api";
import { getProductImageAspectRatio } from "./utils";
import classNames from "classnames";
import AnimationContext from "../contexts/AnimationContext";
import { getAvailableOfferTargetVariantsSet } from "../core/products/variantUtils";

export function productLink(
  product: Product,
  variantTitle: string | undefined,
  display_product_names_as_links: boolean,
  giftQuantity: number | null
) {
  let title = variantTitle;
  const showGiftQuantity = giftQuantity && giftQuantity > 1;
  const giftQuantityNode = showGiftQuantity
    ? `<span id="quantity" class="mr-1 no-underline inline-block">${giftQuantity}×</span>`
    : "";
  if (display_product_names_as_links) {
    title = `<a href="${product.url}">${giftQuantityNode}${title}</a>`;
  } else {
    title = `${showGiftQuantity ? `${giftQuantity}× ` : ""}${title}`;
  }

  return (
    <HTMLSectionWithLink className="GiftBox-Offer-Card-Line-Name GiftBox-Offer-Product-Name Cropped-Text text-gray-900">
      {title}
    </HTMLSectionWithLink>
  );
}

function useVariants(offer, product) {
  const [variant, setVariant] = useState<Variant>(product?.variants[0]);
  const animationContext = useContext(AnimationContext);

  async function changeVariant(event) {
    const variantId = Number(event.target.value);
    const productVariants = availableProductVariants(product, offer);
    const selectedVariant = productVariants?.find((v) => v.id === variantId);

    animationContext.triggerWithAnimation?.({
      name: "on_variant_change_select",
      data: {
        productId: product?.id,
      },
      delayMsBefore: 200,
      delayMsAfter: 0,
      callback: async () => {
        setVariant(selectedVariant as Variant);
        await updateOffer(offer, selectedVariant, offer.shopify_product_handle);
      },
    });
  }

  // Initial variant selection
  useEffect(() => {
    const runInAsync = async () => {
      if (isProductUnavailableOrLoading(product)) return;
      const addedOffer = getAddedOffers().find((o) => o.offerId === offer.id);
      const productVariants = availableProductVariants(product, offer)?.filter(
        (v) => v.available
      );
      const variant = // We need to exclude unavailable variants for default selection
        productVariants?.find(
          (v) => v.id === (addedOffer && addedOffer.productVariantId)
        ) || productVariants?.find((v) => v.available);

      if (variant) {
        await updateOffer(offer, variant);
      }
      setVariant(variant as Variant);
    };
    runInAsync();
  }, [product, offer]);

  const productVariants = availableProductVariants(product, offer) as Variant[];
  return { productVariants, changeVariant, variant };
}

export const ProductCardInner = ({
  offer,
  onEdit,
}: {
  offer: any;
  onEdit?: () => Promise<void>;
}) => {
  const productsCtx = useContext(ProductsContext);
  const product = productsCtx.getProduct(offer.shopify_product_handle);
  const targetProduct = offer.target_shopify_product_handle
    ? productsCtx.getTargetProduct(offer.target_shopify_product_handle)
    : undefined;

  const { productVariants, variant, changeVariant } = useVariants(
    offer,
    product
  );
  const { passed } = useThreshold(offer);

  const availableTargetVariantsSet = getAvailableOfferTargetVariantsSet(
      offer,
      targetProduct
  );
  if (!variant || !product || !productVariants || (offer.target_shopify_product_handle && availableTargetVariantsSet.size === 0)) {
    return null;
  }

  const originalPrice = getProductOriginalPrice(variant);

  const currency = useContext(CurrencyContext);

  const image = (
    <img
      src={withFallback(
        (variant && variant.featured_image && variant.featured_image.src) ||
          variant.image ||
          product?.featured_image
      )}
      alt={product?.title || "Product"}
    />
  );

  let variantTitle =
    offer.shopify_product_variants &&
    offer.shopify_product_variants.length === 1 &&
    variant.title !== "Default Title"
      ? ` - ${variant.title}`
      : null;

  variantTitle = stripHtmlToText(product?.title) + (variantTitle || "");

  const imageAspectRatio = useMemo(
    () => getProductImageAspectRatio(variant, product),
    [variant, product]
  );
  const animationContext = useContext(AnimationContext);

  return (
    <div
      data-cy="giftbox-inner-product-card"
      className="GiftBox-Inner-Product-Card"
    >
      <div className="flex gap-4 items-center">
        <Img
          className={classNames(`[&>img]:aspect-${imageAspectRatio}`, {
            "bg-transparent": imageAspectRatio !== "1",
            "hover:cursor-pointer": true,
          })}
          onClick={() => {
            if (!product?.url) return;
            window.open(product.url, "_parent");
          }}
        >
          {image}

          {passed ? (
            <RegtriggeringAnimatedCheckmark
              color="rgb(17, 168, 118)"
              animationTriggerValue={variant.id}
            />
          ) : null}
        </Img>
        <div className="GiftBox-Offer-Card-Line flex flex-col justify-center Giftbox-CardProductNameWidth">
          {productLink(
            product,
            variantTitle,
            offer.display_product_names_as_links,
            offer.gift_quantity
          )}
          <Prices originalPrice={originalPrice} currency={currency} />
        </div>

        {onEdit && (
          <button
            className="flex items-center cursor-pointer text-gray-600 hover:text-gray-800 p-2.5 ml-auto"
            data-cy="multiple-offer-edit"
            onClick={() =>
              animationContext.triggerWithAnimation?.({
                name: "product_choice_removed",
                delayMsBefore: 150,
                delayMsAfter: 120,
                callback: onEdit,
              })
            }
            tabIndex={0}
            onKeyPress={handleConfirmation(onEdit)}
          >
            <EditIcon />
          </button>
        )}
      </div>
      {passed && productVariants.length > 1 ? (
        <select
          onChange={changeVariant}
          value={variant.id}
          className="h-12 px-4 mt-4 outline-1 outline outline-gray-300 rounded-[10px] w-full border-r-[16px] border-transparent GiftBox-VariantSelector hover:outline-gray-500 hover:cursor-pointer"
        >
          {productVariants.map((v) => (
            <option key={v.id} value={v.id} disabled={!v.available}>
              {v.title.length > 30 ? `${v.title.substring(0, 30)}...` : v.title}
            </option>
          ))}
        </select>
      ) : null}
    </div>
  );
};

function getSubtitle(offer: Offer, settings, passed: boolean | undefined) {
  const subtitle = passed
    ? settings.subtitle_condition_met || "Enjoy your free gifts"
    : offer.subtitle_condition_not_met || "to get a free gift";

  // Note: There is no need to differentiate with REQPRODUCTLINK because REQPRODUCTLINK is used only for the title not subtitle
  const productLinkPlaceholder = "PRODUCTLINK";

  // @deprecated PRODUCTLINK placeholder support
  const [
    deprecatedProductLinkPlaceholderValue,
    setDeprecatedProductLinkPlaceholderValue,
  ] = useState("");

  useEffect(() => {
    // if the product doesnt have the deprecated placeholder "PRODUCTLINK", we avoid making the call to api.
    if (
      !offer?.shopify_product_handle ||
      !subtitle.includes(productLinkPlaceholder)
    ) {
      return;
    }

    getProduct(offer.shopify_product_handle).then((product) => {
      setDeprecatedProductLinkPlaceholderValue(stripHtmlToText(product.title));
    });
  }, [offer?.shopify_product_handle, subtitle]);

  return (
    <HTMLSectionWithLink>
      {subtitle
        .replace("DISPLAYNAME", offer.display_name)
        .replace(productLinkPlaceholder, deprecatedProductLinkPlaceholderValue)}
    </HTMLSectionWithLink>
  );
}

export default ({ offer, settings }: { offer: Offer; settings: Settings }) => {
  const { passed } = useThreshold(offer);
  const productsCtx = useContext(ProductsContext);
  const targetProduct = offer.target_shopify_product_handle
    ? productsCtx.getTargetProduct(offer.target_shopify_product_handle)
    : undefined;
  const availableOfferTargetVariantsSet = getAvailableOfferTargetVariantsSet(
    offer,
    targetProduct
  );

  if (offer.target_shopify_product_handle && availableOfferTargetVariantsSet.size === 0) {
    return null;
  }
  return (
    <Card
      offer={offer}
      subtitle={getSubtitle(offer, settings, passed)}
      isMultipleProduct={false}
    >
      <ProductCardInner offer={offer} />
    </Card>
  );
};
